import React, { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { getAppCopy } from '../i18n';
import { getBillingCopy } from '../i18n/billing';
import { detectLocale } from '../i18n/locale';
import { localizeEntitlementError } from '../i18n/entitlementErrors';
import { issueWebRedeemCode, type IssuedRedeemCode } from '../services/entitlement/platform/nativeRedeem';

type WebRedeemIssueModalProps = {
  open: boolean;
  onClose: () => void;
};

function runTouchAction(e: React.TouchEvent, action: () => void): void {
  e.preventDefault();
  e.stopPropagation();
  action();
}

function formatExpiresAt(iso: string): string {
  if (!iso) return '';
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toLocaleString();
}

export function WebRedeemIssueModal({ open, onClose }: WebRedeemIssueModalProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const autoIssueStartedRef = useRef(false);
  const [busy, setBusy] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const [issued, setIssued] = useState<IssuedRedeemCode | null>(null);
  const [copied, setCopied] = useState(false);

  const handleIssue = useCallback(async () => {
    setBusy(true);
    setErrorMessage(undefined);
    setCopied(false);
    try {
      const result = await issueWebRedeemCode();
      setIssued(result);
    } catch (error) {
      const message = error instanceof Error ? error.message : '';
      const localized = localizeEntitlementError(message, detectLocale());
      setErrorMessage(
        localized || message || getAppCopy().billingErrors.redeemIssueFailed,
      );
      setIssued(null);
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (!open) {
      autoIssueStartedRef.current = false;
      setBusy(false);
      setErrorMessage(undefined);
      setIssued(null);
      setCopied(false);
      return;
    }
    if (autoIssueStartedRef.current) return;
    autoIssueStartedRef.current = true;
    void handleIssue();
  }, [open, handleIssue]);

  useEffect(() => {
    if (!open) return;
    const el = overlayRef.current;
    if (!el) return;
    const blockScroll = (e: TouchEvent) => e.preventDefault();
    el.addEventListener('touchmove', blockScroll, { passive: false });
    return () => el.removeEventListener('touchmove', blockScroll);
  }, [open]);

  if (!open) return null;

  const t = getBillingCopy();

  const handleCopy = async () => {
    if (!issued?.formattedCode) return;
    try {
      await navigator.clipboard.writeText(issued.formattedCode);
      setCopied(true);
    } catch {
      setCopied(false);
    }
  };

  const modal = (
    <div
      ref={overlayRef}
      className="custom-prompt-overlay paywall-overlay"
    >
      <div
        className="custom-prompt-dialog paywall-dialog"
        onClick={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
        role="dialog"
        aria-modal="true"
        aria-labelledby="web-redeem-issue-title"
      >
        <div className="custom-prompt-header">
          <h3 id="web-redeem-issue-title">{t.webRedeemIssueTitle}</h3>
        </div>
        <div className="custom-prompt-body">
          {busy && !issued ? (
            <p className="paywall-platform-note paywall-processing-note">
              {t.webRedeemIssueProcessing}
            </p>
          ) : null}
          {issued ? (
            <>
              <p className="custom-prompt-message">{t.webRedeemIssueResultLead}</p>
              <input
                type="text"
                className="custom-prompt-input paywall-code-display"
                readOnly
                value={issued.formattedCode}
                onFocus={(e) => e.currentTarget.select()}
              />
              {issued.expiresAt ? (
                <p className="paywall-platform-note">
                  {t.webRedeemIssueExpires(formatExpiresAt(issued.expiresAt))}
                </p>
              ) : null}
              <p className="paywall-platform-note">{t.webRedeemIssueNote}</p>
              {copied ? (
                <p className="paywall-platform-note">{t.webRedeemIssueCopied}</p>
              ) : null}
            </>
          ) : null}
          {errorMessage ? <p className="paywall-error">{errorMessage}</p> : null}
          {errorMessage && !issued ? (
            <p className="paywall-platform-note">{t.webRedeemIssueNote}</p>
          ) : null}
        </div>
        <div className="custom-prompt-footer paywall-footer">
          {issued ? (
            <button
              type="button"
              className="custom-prompt-button private"
              disabled={busy}
              onClick={() => void handleCopy()}
              onTouchEnd={(e) => !busy && runTouchAction(e, () => void handleCopy())}
            >
              {t.webRedeemIssueCopy}
            </button>
          ) : null}
          {issued ? (
            <button
              type="button"
              className="custom-prompt-button ok"
              disabled={busy}
              onClick={() => void handleIssue()}
              onTouchEnd={(e) => !busy && runTouchAction(e, () => void handleIssue())}
            >
              {t.webRedeemIssueRegenerate}
            </button>
          ) : null}
          {errorMessage && !issued ? (
            <button
              type="button"
              className="custom-prompt-button ok"
              disabled={busy}
              onClick={() => void handleIssue()}
              onTouchEnd={(e) => !busy && runTouchAction(e, () => void handleIssue())}
            >
              {t.webRedeemIssueRetry}
            </button>
          ) : null}
          <button
            type="button"
            className="custom-prompt-button cancel"
            disabled={busy}
            onClick={onClose}
            onTouchEnd={(e) => !busy && runTouchAction(e, onClose)}
          >
            {t.close}
          </button>
        </div>
      </div>
    </div>
  );

  return createPortal(modal, document.body);
}
